import React, { useEffect, useRef, useState } from "react";
import { heroVideo, heroImage, heroImageBase } from "../data/site";

// Hero background.
//
// The poster image is what the pre-rendered HTML ships, so the hero is complete
// before any JavaScript runs and on any browser that won't play the clip. The
// video is only mounted after the component hydrates, and never for visitors
// who have asked for reduced motion — for them the still image is the hero.
//
// The clip is muted, inline and looping, which is what iOS and Android require
// before they will autoplay anything.
const HeroMedia = ({ alt }) => {
  const [showVideo, setShowVideo] = useState(false);
  const [ready, setReady] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!heroVideo.enabled || typeof window === "undefined") return undefined;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const widthQuery = window.matchMedia(`(min-width: ${heroVideo.minWidth || 0}px)`);
    const dataSaver = navigator.connection?.saveData === true;
    const decide = () =>
      setShowVideo(!motionQuery.matches && widthQuery.matches && !dataSaver);

    decide();
    motionQuery.addEventListener?.("change", decide);
    widthQuery.addEventListener?.("change", decide);
    return () => {
      motionQuery.removeEventListener?.("change", decide);
      widthQuery.removeEventListener?.("change", decide);
    };
  }, []);

  // Fade the video in over the poster once it actually has frames, so a slow
  // connection never shows a black box where the hero should be.
  const onReady = () => setReady(true);

  return (
    <>
      {/* The hero is the largest download on the site, so it ships as WebP
          with a JPEG fallback and three widths. A phone pulls the 768px file
          (~82 KB) instead of the full-width one (~258 KB), which is most of
          the page-weight saving on cellular.

          When the video is on, the poster is still a single JPEG — it is only
          on screen for a moment before the clip fades over it. */}
      {showVideo ? (
        <img
          fetchPriority="high"
          decoding="async"
          src={heroVideo.poster}
          alt={alt}
          className="w-full h-auto lg:h-full lg:object-cover lg:object-[62%_50%]"
        />
      ) : (
        <picture>
          <source
            type="image/webp"
            srcSet={`${heroImageBase}-768.webp 768w, ${heroImageBase}-1200.webp 1200w, ${heroImageBase}-1586.webp 1586w`}
            sizes="100vw"
          />
          <source
            type="image/jpeg"
            srcSet={`${heroImageBase}-768.jpg 768w, ${heroImageBase}-1200.jpg 1200w, ${heroImageBase}-1586.jpg 1586w`}
            sizes="100vw"
          />
          <img
            fetchPriority="high"
            decoding="async"
            src={heroImage}
            alt={alt}
            width="1586"
            height="992"
            // Below lg the photo runs at its own aspect under the copy, so the
            // whole truck and trailer stay in frame — a full-height cover crop
            // on any portrait screen, phone or tablet, cuts the trailer off.
            // From lg up: cover, biased right of center so both vehicles hold
            // the frame while the headline sits over the darkened left.
            className="w-full h-auto lg:h-full lg:object-cover lg:object-[62%_50%]"
          />
        </picture>
      )}
      {showVideo && (
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            ready ? "opacity-100" : "opacity-0"
          }`}
          poster={heroVideo.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          tabIndex={-1}
          onLoadedData={onReady}
          onCanPlay={onReady}
        >
          {heroVideo.sources.map((s) => (
            <source key={s.src} src={s.src} type={s.type} />
          ))}
        </video>
      )}
    </>
  );
};

export default HeroMedia;
